import React, { useState, useMemo, useCallback } from 'react';
import StepPersonalDetails from '../../RetirementCalculatorApp/components/StepPersonalDetails';
import StepRetirementGoals from '../../RetirementCalculatorApp/components/StepRetirementGoals';
import ResultsDashboard from '../../RetirementCalculatorApp/components/ResultsDashboard';
import FrozenScenarioModal from '../../RetirementCalculatorApp/components/FrozenScenarioModal';
import '../../RetirementCalculatorApp/RetirementCalculatorApp.css';
import { soaBuilderAPI } from '../../../services/api';

const CURRENT_YEAR = new Date().getFullYear();

const StepRetirementProjection = ({
  formData,
  updateFormData,
  nextStep,
  prevStep,
  portfolios,
  draftId,
  disabled,
}) => {
  const rpData = formData.retirementProjection || {};
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPercentileIndex, setSelectedPercentileIndex] = useState(2);
  const [viewFrozenScenario, setViewFrozenScenario] = useState(null);

  // ── Live-derived formData adapter ──
  // Merges crossover fields from clientDetails and objectives into retirementProjection
  const derivedFormData = useMemo(() => {
    const rp = formData.retirementProjection || {};
    const client = formData.clientDetails || {};
    const objectives = formData.objectives || {};

    // Couple is always derived from clientDetails
    const couple = client.isCouple ? 'Yes' : 'No';

    // Birth years: use rp override if set, otherwise derive from DOBs
    const birth_year_1 = rp.birth_year_1
      || (client.dateOfBirth ? new Date(client.dateOfBirth).getFullYear() : null);
    const birth_year_2 = rp.birth_year_2
      || (client.partnerDob ? new Date(client.partnerDob).getFullYear() : null);

    // Retirement age: use rp override if set, otherwise derive from objectives
    const retirement_age = rp.retirement_age
      || (objectives.retirementAge ? parseInt(objectives.retirementAge, 10) : 67);

    // Auto-calculate default years in retirement (life expectancy 92 minus retirement age)
    const years = rp.years || Math.max(20, 92 - (retirement_age || 67));

    // Homeowner: derive from clientDetails
    const homeowner = client.homeowner || 'Yes';

    return { ...rp, couple, birth_year_1, birth_year_2, retirement_age, years, homeowner };
  }, [formData.retirementProjection, formData.clientDetails, formData.objectives]);

  // ── Adapter: intercept couple updates → redirect to clientDetails ──
  const retirementUpdateFormData = useCallback((updates) => {
    if ('couple' in updates) {
      updateFormData('clientDetails', { isCouple: updates.couple === 'Yes' });
      const { couple, ...rest } = updates;
      if (Object.keys(rest).length > 0) {
        updateFormData('retirementProjection', rest);
      }
      return;
    }
    updateFormData('retirementProjection', updates);
  }, [updateFormData]);

  // ── Portfolio weights from strategy step ──
  const portfolioWeights = useMemo(() => {
    const strategy = formData.strategy || {};
    const mw = strategy.modelWeights || {};
    const acw = strategy.assetClassWeights || {};
    const fundEntries = strategy.fundEntries || [];
    const mapped = {};

    // Model portfolios: map portfolio_code → blender_code
    for (const [code, w] of Object.entries(mw)) {
      const p = portfolios.find(x => x.portfolio_code === code);
      const blenderCode = p?.blender_code || code;
      mapped[blenderCode] = (mapped[blenderCode] || 0) + (w / 100);
    }

    // Individual funds → blender asset class
    for (const fund of fundEntries) {
      const blenderAc = fund.blender_asset_class;
      if (blenderAc && fund.weight) {
        mapped[blenderAc] = (mapped[blenderAc] || 0) + (fund.weight / 100);
      }
    }

    // Direct asset class weights
    for (const [ac, w] of Object.entries(acw)) {
      mapped[ac] = (mapped[ac] || 0) + (w / 100);
    }

    return mapped;
  }, [formData.strategy, portfolios]);

  // ── Portfolio summary label ──
  const portfolioSummaryLabel = useMemo(() => {
    const strategy = formData.strategy || {};
    const mw = strategy.modelWeights || {};
    const parts = [];
    for (const [code, w] of Object.entries(mw)) {
      if (w > 0) {
        const p = portfolios.find(x => x.portfolio_code === code);
        parts.push(`${p?.display_name || code} ${w}%`);
      }
    }
    const fundEntries = strategy.fundEntries || [];
    for (const fund of fundEntries) {
      if (fund.weight > 0) {
        parts.push(`${fund.fund_name || fund.tr_code} ${fund.weight}%`);
      }
    }
    const acw = strategy.assetClassWeights || {};
    for (const [ac, w] of Object.entries(acw)) {
      if (w > 0) {
        parts.push(`${ac.replace(/([A-Z])/g, ' $1').trim()} ${w}%`);
      }
    }
    return parts.length > 0 ? parts.join(', ') : 'No portfolio set in Strategy step';
  }, [formData.strategy, portfolios]);

  const hasPortfolioWeights = Object.keys(portfolioWeights).length > 0;

  // Fields hidden from StepPersonalDetails — derived from earlier SOA steps
  const HIDDEN_FIELDS = ['couple', 'homeowner', 'birth_year', 'retirement_age', 'years'];

  // ── Calculate ──
  const calculate = useCallback(async () => {
    setIsCalculating(true);
    setError(null);
    try {
      // Calculate ages from birth years
      const age1Calc = CURRENT_YEAR - (derivedFormData.birth_year_1 || CURRENT_YEAR - 67);
      const age2Calc = CURRENT_YEAR - (derivedFormData.birth_year_2 || CURRENT_YEAR - 67);

      const isCouple = derivedFormData.couple === 'Yes';
      const age_1 = isCouple ? Math.max(age1Calc, age2Calc) : age1Calc;
      const age_2 = isCouple ? Math.min(age1Calc, age2Calc) : age1Calc;

      // Filter out invalid lump sum entries
      const cleanedData = {
        ...derivedFormData,
        age_1,
        age_2,
        portfolio_weights: portfolioWeights,
        lump_sum_withdrawals: (derivedFormData.lump_sum_withdrawals || []).filter(
          (w) => w.age !== '' && w.age !== null && !isNaN(w.age) && w.amount !== 0
        ),
      };

      const response = await soaBuilderAPI.calculateRetirement(cleanedData);
      retirementUpdateFormData({ results: response });
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setIsCalculating(false);
    }
  }, [derivedFormData, portfolioWeights, retirementUpdateFormData]);

  // ── Freeze scenario ──
  const freezeScenario = useCallback((name) => {
    if (!derivedFormData.results) return;
    const frozen = derivedFormData.frozenScenarios || [];
    retirementUpdateFormData({
      frozenScenarios: [
        ...frozen,
        {
          name,
          timestamp: new Date().toISOString(),
          results: { ...derivedFormData.results },
          formData: { ...derivedFormData },
        },
      ],
    });
  }, [derivedFormData, retirementUpdateFormData]);

  const removeScenario = useCallback((index) => {
    const frozen = derivedFormData.frozenScenarios || [];
    retirementUpdateFormData({
      frozenScenarios: frozen.filter((_, i) => i !== index),
    });
  }, [derivedFormData.frozenScenarios, retirementUpdateFormData]);

  // ── Save to database ──
  const saveToDatabase = useCallback(async (clientIdentifier, scenarioName) => {
    if (!derivedFormData.results) return null;
    setIsSaving(true);
    setError(null);
    try {
      const saved = await soaBuilderAPI.saveRetirementScenario({
        client_identifier: clientIdentifier,
        scenario_name: scenarioName,
        form_inputs: derivedFormData,
        results_data: derivedFormData.results,
        soa_draft_id: draftId,
      });
      retirementUpdateFormData({ savedScenarioId: saved.id });
      return saved;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [derivedFormData, draftId, retirementUpdateFormData]);

  return (
    <fieldset disabled={disabled}>
      <h4 className="soa-step-title">Retirement Projection</h4>
      <p className="soa-step-subtitle">
        Full retirement modelling using your recommended strategy allocation
      </p>

      {/* Portfolio summary badge */}
      <div className="mb-3 p-3 rounded" style={{
        backgroundColor: hasPortfolioWeights ? '#f0f7ff' : '#fff3cd',
        border: `1px solid ${hasPortfolioWeights ? 'var(--info-color)' : 'var(--warning-color)'}`,
      }}>
        <div className="d-flex align-items-center">
          <i className={`bi ${hasPortfolioWeights ? 'bi-pie-chart-fill' : 'bi-exclamation-triangle'} me-2`}
            style={{ color: hasPortfolioWeights ? 'var(--primary-color)' : 'var(--warning-color)' }}></i>
          <div>
            <div className="small fw-bold" style={{ color: 'var(--primary-color)' }}>
              Portfolio from Strategy
            </div>
            <div className="small text-muted">{portfolioSummaryLabel}</div>
          </div>
        </div>
      </div>

      {/* Derived fields info banner */}
      <div className="mb-3 p-2 rounded small" style={{
        backgroundColor: '#f0f7ff',
        border: '1px solid var(--info-color)',
        color: 'var(--primary-color)',
      }}>
        <i className="bi bi-link-45deg me-1"></i>
        Couple status, birth years, and homeowner are set in <strong>Client Details</strong> (Step 1).
        Retirement age is set in <strong>Objectives</strong> (Step 4).
        Years in retirement is auto-calculated.
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible mb-3">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Wrap retirement components in .retirement-app for CSS scoping */}
      <div className="retirement-app">
        {/* Personal Details */}
        <div className="mb-4">
          <StepPersonalDetails
            formData={derivedFormData}
            updateFormData={retirementUpdateFormData}
            hiddenFields={HIDDEN_FIELDS}
            nextStep={() => {}}
            prevStep={() => {}}
            isLocked={disabled}
          />
        </div>

        {/* Retirement Goals */}
        <div className="mb-4">
          <div className="soa-section-banner">
            <i className="bi bi-bullseye"></i>
            Retirement Goals
          </div>
          <StepRetirementGoals
            formData={derivedFormData}
            updateFormData={retirementUpdateFormData}
            onCalculate={calculate}
            prevStep={() => {}}
            isCalculating={isCalculating}
            isLocked={disabled}
          />
        </div>

        {/* Results Dashboard */}
        {derivedFormData.results && (
          <div className="mb-4">
            <div className="soa-section-banner">
              <i className="bi bi-bar-chart-line"></i>
              Results
            </div>
            <ResultsDashboard
              results={derivedFormData.results}
              formData={derivedFormData}
              selectedPercentileIndex={selectedPercentileIndex}
              setSelectedPercentileIndex={setSelectedPercentileIndex}
              onExportPdf={null}
              frozenScenarios={derivedFormData.frozenScenarios || []}
              savedScenarioId={derivedFormData.savedScenarioId}
              isSaving={isSaving}
              loadedClientIdentifier={formData.clientDetails?.fullName || null}
              isScenarioLocked={false}
              onSaveToDatabase={saveToDatabase}
              onOpenLoadModal={() => {}}
            />
          </div>
        )}

        {/* Frozen scenario comparison */}
        {derivedFormData.frozenScenarios && derivedFormData.frozenScenarios.length > 0 && (
          <div className="mb-4">
            <div className="soa-section-banner">
              <i className="bi bi-layers"></i>
              Saved Scenarios
            </div>
            <div className="row g-2">
              {derivedFormData.frozenScenarios.map((scenario, idx) => (
                <div key={idx} className="col-md-6 col-lg-4">
                  <div className="card h-100">
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong className="small">{scenario.name}</strong>
                        <button className="btn btn-sm p-0" onClick={() => removeScenario(idx)}
                          style={{ color: '#dc3545', lineHeight: 1 }}>
                          <i className="bi bi-x-lg" style={{ fontSize: '0.75rem' }}></i>
                        </button>
                      </div>
                      <div className="small text-muted mb-1">
                        {new Date(scenario.timestamp).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {scenario.results?.probability && (
                        <div className="small">
                          Success: <strong>{scenario.results.probability.success?.toFixed(1)}%</strong>
                        </div>
                      )}
                      <button className="btn btn-sm btn-outline-primary mt-1 w-100"
                        onClick={() => setViewFrozenScenario(scenario)}>
                        <i className="bi bi-eye me-1"></i>View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <FrozenScenarioModal
          scenario={viewFrozenScenario}
          isOpen={!!viewFrozenScenario}
          onClose={() => setViewFrozenScenario(null)}
        />
      </div>

      <div className="soa-step-nav">
        <button className="btn btn-outline-secondary" onClick={prevStep}>
          <i className="bi bi-arrow-left me-1"></i>Back: Strategy
        </button>
        <button className="btn btn-primary" onClick={nextStep}>
          Next: Product Comparison <i className="bi bi-arrow-right ms-1"></i>
        </button>
      </div>
    </fieldset>
  );
};

export default StepRetirementProjection;
